<script setup lang="ts">
import type { ListboxRootEmits, ListboxRootProps } from "reka-ui";
import type { HTMLAttributes } from "vue";
import { cn } from "@/lib/utils.ts";
import { reactiveOmit } from "@vueuse/core";
import { ListboxRoot, useForwardPropsEmits } from "reka-ui";
import { ref, shallowRef } from "vue";
import { provideCommandContext } from ".";

const emits = defineEmits<ListboxRootEmits>();
const props = withDefaults(
  defineProps<ListboxRootProps & { class?: HTMLAttributes["class"] }>(),
  {
    modelValue: "",
  }
);
const delegatedProps = reactiveOmit(props, "class");
const forwarded = useForwardPropsEmits(delegatedProps, emits);

/**
 * The text search input by the user.
 */
const search = ref<string>("");

/**
 * This context content is defined here and then provided to any child components that needs it.
 */
const listboxRef = ref<InstanceType<typeof ListboxRoot> | null>(null);

/** Lets a parent re-highlight the first result, e.g. when results reappear without new input. */
defineExpose({
  highlightFirstItem: () => (listboxRef.value as any)?.highlightFirstItem?.(),
});

provideCommandContext({
  search,
  searchResults: shallowRef([]),
  searchResultsMap: shallowRef(new Map()),
});
</script>

<template>
  <ListboxRoot
    ref="listboxRef"
    v-bind="forwarded"
    :class="
      cn(
        'flex h-full w-full flex-col overflow-hidden rounded-md bg-popover text-popover-foreground',
        props.class
      )
    "
  >
    <slot />
  </ListboxRoot>
</template>
